/* Clever API & Widgets landing (/api, guests): theme toggles for the two
   fake widget previews, the scroll reveals and the html.js stamp that
   arms the hero animation loop. The page renders complete without this
   file -- nothing is hidden -- so everything here is progressive
   enhancement. No embed or badge snippets live here: the copyable code
   sits behind the login on the account's API page.

   The FAQ accordion is not here: the page uses the site's own .faq markup,
   which ios-cleaner.js already drives. */
(function () {
    'use strict';

    var host = document.querySelector('.cwidgets');
    if (!host) return;

    /* Arms the CSS-only extras: the hero "how it works" loop and the
       reveal-on-scroll initial state. Both stay inert without this class. */
    document.documentElement.classList.add('js');

    /* ---------- scroll reveals ---------- */
    var reveals = host.querySelectorAll('.reveal');
    if ('IntersectionObserver' in window) {
        var revealIO = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in');
                    revealIO.unobserve(entry.target);
                }
            });
        }, { threshold: .12 });
        reveals.forEach(function (el) { revealIO.observe(el); });
    } else {
        reveals.forEach(function (el) { el.classList.add('in'); });
    }

    /* ---------- the fake results come alive on scroll ----------
       The mock's live widgets fill their gauge, slide the meter pin and pop
       the verdict when they finish a check. Our previews are static markup,
       so the same show is staged here: each card is wound back to zero the
       moment JS runs, then played once it scrolls into view. Under reduced
       motion (or without IntersectionObserver) nothing is touched and the
       markup's finished state stands. */
    var reduceMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function armDetector(card) {
        var res = card.querySelector('.cwp-res');
        var gauge = card.querySelector('.cwp-gauge');
        var num = card.querySelector('.cwp-gnum');
        var pin = card.querySelector('.cwp-track i');
        var verdict = card.querySelector('.cwp-verdict');
        var btn = card.querySelector('.cwp-btn');
        if (!res || !gauge || !num) return null;
        var target = parseFloat(gauge.style.getPropertyValue('--pct')) || 87;
        var pinLeft = pin ? pin.style.left : '';
        res.classList.add('waiting');
        gauge.style.setProperty('--pct', 0);
        num.innerHTML = '0<small>%</small>';
        if (pin) pin.style.left = '0%';
        if (verdict) verdict.classList.add('waiting');
        if (btn) btn.textContent = btn.dataset.labelRun || 'Check for AI';
        return function () {
            res.classList.remove('waiting');
            var start = null, dur = 1400;
            function frame(ts) {
                if (start === null) start = ts;
                var p = Math.min((ts - start) / dur, 1);
                var eased = 1 - Math.pow(1 - p, 3);
                gauge.style.setProperty('--pct', target * eased);
                num.innerHTML = Math.round(target * eased) + '<small>%</small>';
                if (p < 1) {
                    window.requestAnimationFrame(frame);
                } else {
                    gauge.classList.add('done');
                    if (btn) btn.textContent = btn.dataset.labelAgain || 'Check again';
                }
            }
            window.requestAnimationFrame(frame);
            if (pin) pin.style.left = pinLeft; // the CSS transition carries it
            if (verdict) {
                window.setTimeout(function () {
                    verdict.classList.add('shown');
                    verdict.classList.remove('waiting');
                }, 800);
            }
        };
    }

    /* The mock's humanizer thinks out loud, then types its rewrite: the
       "Rewriting in a human voice..." shimmer for a beat, then the result
       arrives four characters at a time, like the live widget's stream. */
    function armHumanizer(card) {
        var res = card.querySelector('.cwp-res');
        var out = card.querySelector('.cwp-out');
        var btn = card.querySelector('.cwp-btn');
        if (!res || !out) return null;
        var full = out.textContent;
        res.classList.add('waiting');
        if (btn) btn.textContent = btn.dataset.labelRun || 'Humanize';
        return function () {
            res.classList.remove('waiting');
            if (btn) btn.textContent = btn.dataset.labelBusy || 'Humanizing…';
            out.classList.add('thinking');
            out.textContent = out.dataset.labelThinking || 'Rewriting in a human voice…';
            window.setTimeout(function () {
                out.classList.remove('thinking');
                out.textContent = '';
                var shown = 0;
                var typer = window.setInterval(function () {
                    shown += 4;
                    out.textContent = full.slice(0, shown);
                    if (shown >= full.length) {
                        window.clearInterval(typer);
                        if (btn) btn.textContent = btn.dataset.labelAgain || 'Humanize again';
                    }
                }, 12);
            }, 800);
        };
    }

    if (!reduceMotion && 'IntersectionObserver' in window) {
        var plays = [];
        host.querySelectorAll('.cwp').forEach(function (card) {
            var play = card.querySelector('.cwp-gauge') ? armDetector(card) : armHumanizer(card);
            if (play) plays.push({ card: card, play: play });
        });
        if (plays.length) {
            var resultIO = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (!entry.isIntersecting) return;
                    resultIO.unobserve(entry.target);
                    plays.forEach(function (item) {
                        if (item.card === entry.target) {
                            window.setTimeout(item.play, 250);
                        }
                    });
                });
            }, { threshold: .35 });
            plays.forEach(function (item) { resultIO.observe(item.card); });
        }
    }

    /* ---------- live embeds: real height from the real widget ----------
       Same handshake as the platform's own loader: post clever:init with a
       private channel, then follow the widget's clever:resize messages so
       the iframe is always exactly as tall as the widget -- compact while
       empty, roomy once a result lands. */
    /* the page's used colour scheme; "light dark" resolves by the OS, as the
       browser itself would */
    function hostScheme() {
        var declared = getComputedStyle(document.documentElement).colorScheme || '';
        return declared.indexOf('dark') !== -1
            && (declared.indexOf('light') === -1 || window.matchMedia('(prefers-color-scheme: dark)').matches)
            ? 'dark' : 'light';
    }
    var liveFrames = [];
    host.querySelectorAll('iframe[data-live-embed]').forEach(function (frame) {
        var origin = new URL(frame.dataset.liveEmbed).origin;
        var channel = (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID()
            : 'cw-' + Math.random().toString(36).slice(2) + '-' + Date.now();
        liveFrames.push({ frame: frame, channel: channel, origin: origin });
        var hello = function () {
            if (frame.contentWindow) {
                /* The scheme rides along so the widget's canvas can match the
                   page and stay transparent: on a mismatch the browser paints
                   it opaque -- white corners around a dark card. */
                frame.contentWindow.postMessage({ type: 'clever:init', channel: channel, scheme: hostScheme() }, origin);
            }
        };
        frame.addEventListener('load', hello);
        hello(); // the frame may have finished loading before this script ran
    });
    if (liveFrames.length) {
        /* The site's theme button flips data-theme on <html>; the frames are
           already alive by then, so each flip re-runs the handshake with the
           new scheme -- otherwise a viewer who switches to dark keeps a white
           canvas behind the cards until reload. */
        new MutationObserver(function () {
            liveFrames.forEach(function (item) {
                if (item.frame.contentWindow) {
                    item.frame.contentWindow.postMessage({ type: 'clever:init', channel: item.channel, scheme: hostScheme() }, item.origin);
                }
            });
        }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        window.addEventListener('message', function (event) {
            liveFrames.forEach(function (item) {
                if (event.source !== item.frame.contentWindow || event.origin !== item.origin) return;
                if (!event.data || event.data.type !== 'clever:resize' || event.data.channel !== item.channel) return;
                var height = event.data.height;
                if (typeof height === 'number' && isFinite(height) && height >= 100 && height <= 20000) {
                    /* The first message is the widget telling us how tall it
                       actually is, and it lands after the frame has already
                       been sitting at the placeholder height in the markup --
                       so on load the difference showed as a gap under the
                       buttons, large or small depending on the widget. That
                       one snaps into place; everything after it eases, which
                       is what the animation is for. */
                    item.frame.style.transitionDuration = item.sized ? '' : '0s';
                    /* as-is, no rounding: a ceil left a sub-pixel thread of
                       bare canvas under the card */
                    item.frame.style.height = height + 'px';
                    item.frame.style.opacity = '1';
                    item.sized = true;
                }
            });
        });
    }

    /* Shared behaviour for every .seg pill group: one active pill,
       aria-pressed follows it, the callback gets the picked value. */
    function segGroup(el, onPick) {
        if (!el) return;
        var pills = el.querySelectorAll('button[data-v]');
        pills.forEach(function (pill) {
            pill.addEventListener('click', function () {
                pills.forEach(function (p) {
                    p.classList.toggle('active', p === pill);
                    p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
                });
                onPick(pill.dataset.v);
            });
        });
    }

    /* ---------- preview theme toggles ---------- */
    document.querySelectorAll('.seg[data-preview-theme]').forEach(function (seg) {
        var card = document.getElementById(seg.dataset.previewTheme);
        if (!card) return;
        segGroup(seg, function (theme) {
            // A live embed reloads with the theme in its URL; the static
            // mock just flips its palette and pops.
            if (card.dataset.liveEmbed) {
                card.src = card.dataset.liveEmbed + '?theme=' + theme;
                return;
            }
            card.setAttribute('data-theme', theme);
            card.classList.remove('pop');
            void card.offsetWidth; // restart the animation on every flip
            card.classList.add('pop');
        });
    });
})();
